/**
 * Generator 2: Server actions — admin/lib/actions/<name>/
 * Generates 'use server' CRUD actions for an entity schema,
 * split into individual files with a barrel index.ts.
 */

import { getManyToManyFields } from '@core-engine/schema/field-helpers/index.js'
import { isUserRelationshipTarget } from '@core-engine/schema/system-relationships.js'
import type { Schema, SchemaField } from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import {
  pluralize,
  quotePropertyName,
  singularize,
  toCamelCase,
  toKebabCase,
  toPascalCase
} from '@core-engine/utils/string.js'
import { resolveSchemaActionDir } from '@next-adapter/config/paths.js'
import type { ActionsGeneratorResult } from '@next-adapter/generators/actions/action-helpers.js'
import {
  buildAuthorshipDataFields,
  buildAuthorshipJoins,
  buildAuthorshipSelects,
  buildAuthorshipUserType,
  buildExplicitSelect,
  buildReadSelectOptionType,
  buildRelationshipDataTypes,
  buildRelationshipSelect,
  buildResultMapping,
  buildSingleRowMapping,
  collectReadSelectFields,
  findListFieldsWithRelationships,
  generateFieldMapping,
  generatePopulateListRelsFunction,
  getFieldType,
  getInputFieldType,
  getReadFieldType,
  isAuthorshipFieldName
} from '@next-adapter/generators/actions/action-helpers.js'
import type { EntityCtx } from '@next-adapter/generators/actions/entity-file-contents.js'
import {
  genBarrelContent,
  genBulkSortOrderContent,
  genCreateContent,
  genCreateSelectOptionContent,
  genDeleteBulkContent,
  genDeleteContent,
  genDistinctFiles,
  genGetByIdContent,
  genGetBySlugContent,
  genGetPluralContent,
  genGetSelectOptionsContent,
  genGetVersionsContent,
  genHelpersContent,
  genM2MFiles,
  genRestoreContent,
  genTypesContent,
  genUpdateContent
} from '@next-adapter/generators/actions/entity-file-contents.js'
import { genMarkdownRendererContent } from '@next-adapter/generators/actions/markdown-renderer.js'
import {
  getEntityFieldContext,
  getFilterableFields,
  resolveEntityFilters
} from '@next-adapter/generators/entity-filters.js'

/**
 * Generate server action files for an entity, split into individual files.
 */
export function generateActions(
  schema: Schema,
  actionsDir: string,
  options: {
    force?: boolean
    schemasDir?: string
    nextMajorVersion?: number
    outputNamespace?: string
  } = {}
): ActionsGeneratorResult {
  const ctx = buildEntityContext(schema, options.nextMajorVersion)
  const schemaActionDir = resolveSchemaActionDir(actionsDir, schema.name, options.outputNamespace)

  // Build all file entries
  const kebabSingular = toKebabCase(ctx.singular)
  const kebabPlural = toKebabCase(ctx.plural)
  const files: Array<{ name: string; content: string }> = []

  files.push({ name: 'types.ts', content: genTypesContent(ctx) })

  const helpersContent = genHelpersContent(ctx)
  if (helpersContent) {
    files.push({ name: 'helpers.ts', content: helpersContent })
  }
  if (ctx.hasHtmlOutput) {
    files.push({ name: 'markdown.ts', content: genMarkdownRendererContent() })
  }

  files.push({ name: `get-${kebabPlural}.ts`, content: genGetPluralContent(ctx) })
  files.push({ name: `get-${kebabSingular}-by-id.ts`, content: genGetByIdContent(ctx) })
  if (ctx.hasCreatableSelectFields) {
    files.push({
      name: `get-${kebabSingular}-select-options.ts`,
      content: genGetSelectOptionsContent(ctx)
    })
    files.push({
      name: `create-${kebabSingular}-select-option.ts`,
      content: genCreateSelectOptionContent(ctx)
    })
  }

  const bySlugContent = genGetBySlugContent(ctx)
  if (bySlugContent) {
    files.push({ name: `get-${kebabSingular}-by-slug.ts`, content: bySlugContent })
  }

  files.push({ name: `create-${kebabSingular}.ts`, content: genCreateContent(ctx) })
  files.push({ name: `update-${kebabSingular}.ts`, content: genUpdateContent(ctx) })
  files.push({
    name: `get-versions-by-${kebabSingular}-id.ts`,
    content: genGetVersionsContent(ctx)
  })
  files.push({ name: `restore-${kebabSingular}-version.ts`, content: genRestoreContent(ctx) })
  files.push({ name: `delete-${kebabSingular}.ts`, content: genDeleteContent(ctx) })
  files.push({ name: `delete-bulk-${kebabPlural}.ts`, content: genDeleteBulkContent(ctx) })
  files.push({
    name: `bulk-update-${kebabPlural}-sort-order.ts`,
    content: genBulkSortOrderContent(ctx)
  })

  // M2M files (conditional, per relationship)
  files.push(...genM2MFiles(ctx))

  // Distinct filter files (conditional, per filter field)
  files.push(...genDistinctFiles(ctx))

  // Barrel index.ts (must be last, references other files)
  files.push({ name: 'index.ts', content: genBarrelContent(files, ctx) })

  return {
    files: files.map((file) => createGeneratedFile(`${schemaActionDir}/${file.name}`, file.content))
  }
}

// ============================================================================
// Context builder — computes all shared state for content generators
// ============================================================================

function buildEntityContext(schema: Schema, nextMajorVersion?: number): EntityCtx {
  const singular = singularize(schema.name)
  const plural = pluralize(schema.name)
  const Singular = toPascalCase(singular)
  const Plural = toPascalCase(plural)
  const tableVar = toCamelCase(schema.name)
  const tableName = toPascalCase(schema.name)
  const camelPlural = toCamelCase(plural)
  const camelSingular = toCamelCase(singular)

  const { dbFields, regularDbFields, allDbFields } = getEntityFieldContext(schema)
  const m2mFields = getManyToManyFields(schema.fields)
  const hasM2M = m2mFields.length > 0

  const relationshipFields = dbFields.filter(
    (f) => f.type === 'relationship' && f.relationship && !f.multiple
  )
  const hasRelationships = relationshipFields.length > 0

  const listFieldsWithRels = findListFieldsWithRelationships(dbFields)
  const hasListRels = listFieldsWithRels.length > 0

  // Media field detection
  const mediaFields = regularDbFields.filter(
    (f) => f.type === 'image' || f.type === 'video' || f.type === 'media'
  )
  const galleryFields = regularDbFields.filter((f) => f.type === 'gallery')
  const hasMediaFields = mediaFields.length > 0 || galleryFields.length > 0

  const allListRelQueries: Array<{
    fieldPath: string
    relField: SchemaField
    relTable: string
    listFieldName: string
  }> = []

  for (const { field: listField, path: fieldPath } of listFieldsWithRels) {
    const rels = (listField.fields || []).filter((f) => f.type === 'relationship' && f.relationship)
    for (const relField of rels) {
      allListRelQueries.push({
        fieldPath: fieldPath.join('_'),
        relField,
        relTable: isUserRelationshipTarget(relField.relationship)
          ? 'user'
          : toCamelCase(relField.relationship!),
        listFieldName: listField.name
      })
    }
  }

  const populateListRelsFn = hasListRels
    ? generatePopulateListRelsFunction(allListRelQueries, Singular)
    : ''

  const htmlOutputFields = regularDbFields.filter(
    (f) => (f.type === 'richtext' || f.type === 'markdown') && f.output === 'html'
  )
  const hasHtmlOutput = htmlOutputFields.length > 0

  const hasSlug = regularDbFields.some((f) => f.name === 'slug')
  const slugSourceField = regularDbFields.some(
    (f) => f.name === 'title' && ['string', 'varchar', 'text'].includes(f.type)
  )
    ? 'title'
    : undefined
  const hasDraft = schema.actions?.draft === true
  const hasSearch = (schema.search?.fields || []).length > 0
  const searchFields = schema.search?.fields || []
  const resolvedFilters = resolveEntityFilters(schema)
  const hasFilters = resolvedFilters.length > 0
  const creatableSelectFields = regularDbFields.filter(
    (f) => f.type === 'select' && f.creatable === true
  )
  const hasCreatableSelectFields = creatableSelectFields.length > 0
  const selectFields = collectReadSelectFields(allDbFields)
  const hasSelectFields = selectFields.length > 0
  const dateRangeFilterFields = new Set(
    resolvedFilters.filter((filter) => filter.type === 'date-range').map((filter) => filter.field)
  )

  const listExcludeFields = new Set(htmlOutputFields.map((f) => f.name))
  const listDbFields = hasHtmlOutput
    ? allDbFields.filter((f) => !listExcludeFields.has(f.name))
    : allDbFields

  const createFields = regularDbFields.filter(
    (f) =>
      !f.primaryKey &&
      f.name !== 'createdAt' &&
      f.name !== 'updatedAt' &&
      f.name !== 'sortOrder' &&
      !isAuthorshipFieldName(f.name)
  )

  const versionSnapshotKeys = [
    ...createFields.map((field) => field.name),
    ...htmlOutputFields.map((field) => `${field.name}Html`),
    ...m2mFields.map((field) => field.name),
    ...(hasDraft ? ['published'] : [])
  ]
  const versionRestoreKeys = [
    ...createFields.map((field) => field.name),
    ...htmlOutputFields.map((field) => `${field.name}Html`),
    ...(hasDraft ? ['published'] : [])
  ]

  const filterableFields = getFilterableFields(allDbFields)

  // Per-file DB imports
  const relTableImports = Array.from(
    new Set(
      relationshipFields
        .filter((field) => !isUserRelationshipTarget(field.relationship))
        .map((field) => toCamelCase(field.relationship!))
    )
  )
  const listRelTableImports = Array.from(new Set(allListRelQueries.map((query) => query.relTable)))

  // --- Build interfaces ---
  const dataFields = allDbFields
    .map(
      (f) =>
        `  ${quotePropertyName(f.name)}: ${getReadFieldType(f)}${
          f.type === 'select' || f.type === 'relationship' || f.required || f.primaryKey
            ? ''
            : ' | null'
        }`
    )
    .join('\n')
  const m2mFieldTypes = m2mFields.map((f) => `  ${quotePropertyName(f.name)}: string[]`).join('\n')
  const htmlFieldTypes = htmlOutputFields.map((f) => `  ${f.name}Html: string`).join('\n')
  const mediaCompanionTypes = [
    ...mediaFields.map((f) => `  ${quotePropertyName(`${f.name}Media`)}: AdminMedia | null`),
    ...galleryFields.map((f) => `  ${quotePropertyName(`${f.name}Media`)}: AdminMedia[]`)
  ].join('\n')

  const mediaTypeImport = hasMediaFields ? `\nimport type { AdminMedia } from '@admin/types'\n` : ''
  const hasTimestampFields =
    allDbFields.some((f) => f.name === 'createdAt') &&
    allDbFields.some((f) => f.name === 'updatedAt')
  const hasFormattedTimestampFields = allDbFields.some((f) => f.name === 'createdAtFormatted')
  const timestampFields =
    hasTimestampFields && !hasFormattedTimestampFields
      ? '\n  createdAtFormatted: string\n  updatedAtFormatted: string'
      : ''
  const readSelectOptionType = buildReadSelectOptionType(selectFields)
  const relationshipDataTypes = buildRelationshipDataTypes(allDbFields)
  const dataInterface = `${mediaTypeImport}${buildAuthorshipUserType()}${readSelectOptionType ? `\n\n${readSelectOptionType}` : ''}${relationshipDataTypes ? `\n\n${relationshipDataTypes}` : ''}\n\nexport interface ${Singular} {\n${dataFields}${htmlFieldTypes ? `\n${htmlFieldTypes}` : ''}${m2mFieldTypes ? `\n${m2mFieldTypes}` : ''}${mediaCompanionTypes ? `\n${mediaCompanionTypes}` : ''}${timestampFields}\n${buildAuthorshipDataFields()}\n}`

  const responseInterface = `export interface ${Plural}Page {\n  ${camelPlural}: ${Singular}[]\n  total: number\n}`

  const filtersInterfaceFields = filterableFields
    .map((f) => {
      if (!dateRangeFilterFields.has(f.name)) {
        return `  ${quotePropertyName(f.name)}?: ${getFieldType(f)}`
      }

      return [
        `  ${quotePropertyName(f.name)}?: ${getFieldType(f)}`,
        `  ${quotePropertyName(`${f.name}From`)}?: ${getFieldType(f)}`,
        `  ${quotePropertyName(`${f.name}To`)}?: ${getFieldType(f)}`
      ].join('\n')
    })
    .join('\n')
  const filtersInterface = `export interface ${Plural}Query {\n  search?: string\n${filtersInterfaceFields}\n  limit?: number\n  offset?: number\n  orderBy?: string\n  orderDirection?: 'asc' | 'desc'\n}`

  const createInterfaceFields = createFields
    .map(
      (f) =>
        `  ${quotePropertyName(f.name)}${f.required && f.name !== 'slug' ? '' : '?'}: ${getInputFieldType(f)}`
    )
    .join('\n')
  const createInterface = `export interface ${Singular}CreateInput {\n${createInterfaceFields}${hasDraft ? `\n  published?: boolean` : ''}\n}`

  const updateInterfaceFields = createFields
    .map((f) => `  ${quotePropertyName(f.name)}?: ${getInputFieldType(f)}`)
    .join('\n')
  const updateInterface = `export interface ${Singular}UpdateInput {\n  id: string\n${updateInterfaceFields}${hasDraft ? `\n  published?: boolean` : ''}\n}`

  // --- Build select clauses ---
  const authorshipSelects = buildAuthorshipSelects(hasRelationships ? '        ' : '      ')
  const authorshipJoins = buildAuthorshipJoins(tableVar)

  const selectClause = hasRelationships
    ? buildRelationshipSelect(
        allDbFields,
        relationshipFields,
        tableVar,
        htmlOutputFields,
        authorshipSelects,
        authorshipJoins
      )
    : buildExplicitSelect(
        allDbFields,
        tableVar,
        htmlOutputFields,
        authorshipSelects,
        authorshipJoins
      )

  const listSelectClause = hasHtmlOutput
    ? hasRelationships
      ? buildRelationshipSelect(
          listDbFields,
          relationshipFields,
          tableVar,
          [],
          authorshipSelects,
          authorshipJoins
        )
      : buildExplicitSelect(listDbFields, tableVar, [], authorshipSelects, authorshipJoins)
    : selectClause

  // --- Build query parts ---
  const filterConditions = filterableFields
    .map((f) => {
      if (dateRangeFilterFields.has(f.name)) {
        return `    if (query?.${f.name}) {\n      conditions.push(eq(${tableVar}.${f.name}, query.${f.name}))\n    }\n    if (query?.${f.name}From) {\n      conditions.push(gte(${tableVar}.${f.name}, query.${f.name}From))\n    }\n    const ${f.name}ExclusiveTo = getExclusiveDateUpperBound(query?.${f.name}To)\n    if (${f.name}ExclusiveTo) {\n      conditions.push(lt(${tableVar}.${f.name}, ${f.name}ExclusiveTo))\n    }`
      }

      return f.type === 'boolean'
        ? `    if (query?.${f.name} !== undefined) {\n      conditions.push(eq(${tableVar}.${f.name}, query.${f.name}))\n    }`
        : `    if (query?.${f.name}) {\n      conditions.push(eq(${tableVar}.${f.name}, query.${f.name}))\n    }`
    })
    .join('\n')

  const searchBlock = hasSearch
    ? `    const search = query?.search\n    if (search && typeof search === 'string' && search.trim()) {\n      const searchTerm = \`%\${search.trim().toLowerCase()}%\`\n      conditions.push(\n        or(\n${searchFields.map((f) => `          ilike(${tableVar}.${f}, searchTerm)`).join(',\n')}\n        )\n      )\n    }\n`
    : ''

  const baseResultMapping = buildResultMapping(
    allDbFields,
    relationshipFields,
    Plural,
    camelPlural,
    Singular,
    hasListRels
  )

  const selectResultMapping = hasSelectFields
    ? wrapWithSelectResolution(baseResultMapping, camelPlural, Singular)
    : baseResultMapping

  // Wrap with media resolution if needed
  const resultMapping = hasMediaFields
    ? wrapWithMediaResolution(selectResultMapping, camelPlural, Singular)
    : selectResultMapping

  const baseListResultMapping = hasHtmlOutput
    ? buildResultMapping(
        listDbFields,
        relationshipFields,
        Plural,
        camelPlural,
        Singular,
        hasListRels
      )
    : baseResultMapping

  const selectListResultMapping = hasSelectFields
    ? wrapWithSelectResolution(baseListResultMapping, camelPlural, Singular)
    : baseListResultMapping

  const listResultMapping = hasMediaFields
    ? wrapWithMediaResolution(selectListResultMapping, camelPlural, Singular)
    : selectListResultMapping

  const fieldMeta = createFields
    .map((f) => `{ name: '${f.name}', type: '${f.type}', required: ${f.required ?? false} }`)
    .join(',\n    ')

  const createMappings = createFields
    .map((f) => `      ${f.name}: ${f.name === 'slug' ? 'resolvedSlug' : generateFieldMapping(f)}`)
    .join(',\n')

  const htmlCreateBlock = hasHtmlOutput
    ? `\n` +
      htmlOutputFields
        .map((f) => `    const ${f.name}Html = renderMarkdownSync(input.${f.name} || '')`)
        .join('\n') +
      '\n'
    : ''

  const htmlCreateMappings = htmlOutputFields.map((f) => `      ${f.name}Html`).join(',\n')

  const cacheTag = `${plural}:all`

  // --- Build single row returns ---
  const baseSingleRowReturn = (() => {
    const singleRowMapping = buildSingleRowMapping(
      allDbFields,
      relationshipFields,
      Singular,
      htmlOutputFields
    )
    if (hasRelationships && hasListRels) {
      return `const row = result[0]\n    return await populate${Singular}ListRelationships(${singleRowMapping})`
    }
    if (hasRelationships) {
      return `const row = result[0]\n    return ${singleRowMapping}`
    }
    if (hasListRels) {
      return `const row = result[0]\n    return await populate${Singular}ListRelationships(${singleRowMapping})`
    }
    return `const row = result[0]\n    return ${singleRowMapping}`
  })()

  const singleRowResolvers = [
    ...(hasSelectFields ? [`resolve${Singular}SelectFields`] : []),
    ...(hasMediaFields ? [`resolve${Singular}MediaFields`] : [])
  ]
  const singleRowReturn =
    singleRowResolvers.length > 0
      ? wrapSingleRowReturnWithResolvers(baseSingleRowReturn, singleRowResolvers)
      : baseSingleRowReturn

  const nextMajor = nextMajorVersion ?? 16
  const cacheInvalidationFn = nextMajor >= 16 ? 'updateTag' : 'revalidateTag'
  const cacheReadImport =
    nextMajor >= 16
      ? `import { cacheLife, cacheTag } from 'next/cache'`
      : `import { unstable_cacheTag as cacheTag } from 'next/cache'`
  const cacheLifeLine = nextMajor >= 16 ? `\n  cacheLife('max')` : ''

  return {
    tableName,
    singular,
    plural,
    Singular,
    Plural,
    tableVar,
    camelSingular,
    camelPlural,
    cacheTag,
    cacheReadImport,
    cacheLifeLine,
    cacheInvalidationFn,
    hasRelationships,
    hasM2M,
    hasListRels,
    hasMediaFields,
    mediaFields,
    galleryFields,
    hasHtmlOutput,
    hasSlug,
    slugSourceField,
    hasDraft,
    hasSearch,
    hasFilters,
    hasSelectFields,
    hasCreatableSelectFields,
    searchFields,
    htmlOutputFields,
    selectFields,
    creatableSelectFields,
    m2mFields,
    filterableFields,
    relTableImports,
    listRelTableImports,
    dataInterface,
    responseInterface,
    filtersInterface,
    createInterface,
    updateInterface,
    selectClause,
    listSelectClause,
    filterConditions,
    searchBlock,
    resultMapping,
    listResultMapping,
    singleRowReturn,
    fieldMeta,
    createMappings,
    htmlCreateBlock,
    htmlCreateMappings,
    populateListRelsFn,
    filters: resolvedFilters,
    sortableColumns: (schema.columns ?? [])
      .filter((col) => col.sortable === true)
      .map((col) => col.accessorKey),
    selectOptionsTableVar: `${tableVar}SelectOptions`,
    versionSnapshotKeys,
    versionRestoreKeys
  }
}

/**
 * Wrap result mapping with media resolution call.
 * Finds the return statement and inserts a resolveMediaFields call before it.
 */
function wrapWithMediaResolution(mapping: string, camelPlural: string, Singular: string): string {
  // Replace `return { camelPlural: <array>, total }` with
  // `const resolved = await resolveMediaFields(<array>); return { camelPlural: resolved, total }`
  const returnPattern = new RegExp(
    `return \\{\\n\\s+${camelPlural}: ([^,]+),\\n\\s+total\\n\\s+\\}`
  )
  const match = mapping.match(returnPattern)
  if (match) {
    const arrayExpr = match[1].trim()
    return mapping.replace(
      match[0],
      `const resolved${Singular}Media = await resolve${Singular}MediaFields(${arrayExpr})\n\n    return {\n      ${camelPlural}: resolved${Singular}Media,\n      total\n    }`
    )
  }
  // Fallback: simple return with results as Type[]
  return mapping.replace(
    new RegExp(`${camelPlural}: (results as ${Singular}\\[\\])`),
    `${camelPlural}: await resolve${Singular}MediaFields($1 as unknown as ${Singular}[])`
  )
}

function wrapWithSelectResolution(mapping: string, camelPlural: string, Singular: string): string {
  const returnPattern = new RegExp(
    `return \\{\\n\\s+${camelPlural}: ([^,]+),\\n\\s+total\\n\\s+\\}`
  )
  const match = mapping.match(returnPattern)
  if (match) {
    const arrayExpr = match[1].trim()
    return mapping.replace(
      match[0],
      `const resolved${Singular}SelectFields = await resolve${Singular}SelectFields(${arrayExpr})\n\n    return {\n      ${camelPlural}: resolved${Singular}SelectFields,\n      total\n    }`
    )
  }

  return mapping.replace(
    new RegExp(`${camelPlural}: (results as ${Singular}\\[\\])`),
    `${camelPlural}: await resolve${Singular}SelectFields($1 as unknown as ${Singular}[])`
  )
}

function wrapSingleRowReturnWithResolvers(singleRowReturn: string, resolvers: string[]): string {
  return singleRowReturn.replace(/return ([\s\S]+)$/, (_, expression: string) => {
    const lines = [`const singleRow = ${expression}`]
    let current = 'singleRow'

    for (const resolver of resolvers) {
      const resolvedName = resolver.replace(/^resolve/, 'resolved').replace(/Fields$/, 'Row')
      lines.push(`    const [${resolvedName}] = await ${resolver}([${current}])`)
      current = resolvedName
    }

    lines.push(`    return ${current}`)
    return lines.join('\n')
  })
}
